import { getSupabaseClient } from '../../../../core/supabase/client.js';
import { fetchAllPaged } from '@/core/supabase/pagination.js';
import type { CreateUserPayload } from '../../domain/repositories/IMembershipRepository.js';

function toCreateUserBody(u: CreateUserPayload) {
  return {
    email: u.email,
    password: u.password,
    firstName: u.firstName ?? null,
    lastName: u.lastName ?? null,
    phone: u.phone ?? null,
    orgId: u.orgId ?? null,
    orgRoleId: u.orgRoleId ?? null,
    projectId: u.projectId ?? null,
    projectRoleId: u.projectRoleId ?? null,
    provinceCode: u.provinceCode ?? null,
    birthDate: u.birthDate ?? null,
    gender: u.gender ?? null,
    permanentAddress: u.permanentAddress ?? null,
    contactAddress: u.contactAddress ?? null,
    idCardNumber: u.idCardNumber ?? null,
    taxCode: u.taxCode ?? null,
    bankAccountNumber: u.bankAccountNumber ?? null,
    bankName: u.bankName ?? null,
    bankBranch: u.bankBranch ?? null,
    heightCm: u.heightCm ?? null,
    weightKg: u.weightKg ?? null,
    workExperiences: u.workExperiences ?? null,
  };
}

async function extractEdgeFunctionError(error: any, fallback: string): Promise<string> {
  // supabase-js wraps non-2xx responses in FunctionsHttpError with a generic
  // message; the real body is on error.context (a Response object).
  try {
    const ctx = error?.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.clone().json();
      if (body?.error) return String(body.error);
      if (body?.message) return String(body.message);
    }
    if (ctx && typeof ctx.text === 'function') {
      const txt = await ctx.clone().text();
      if (txt) return txt;
    }
  } catch { /* fall through */ }
  return error?.message ?? fallback;
}

export class MembershipRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async listMemberships(orgId: string, projectId?: string, roleId?: string, limit?: number, offset?: number, profileId?: string): Promise<{ data: any[]; total: number }> {
    let query = this.supabase
      .from('memberships')
      .select(`id, profile_id, org_id, project_id, role_id, created_at, metadatas, profile:profiles(email, first_name, last_name, phone), role:roles(name)`, { count: 'exact' })
      .eq('org_id', orgId);
    if (projectId) query = query.eq('project_id', projectId);
    if (roleId) query = query.eq('role_id', roleId);
    if (profileId) query = query.eq('profile_id', profileId);
    query = query.order('created_at', { ascending: false });
    if (typeof limit === 'number' && typeof offset === 'number') {
      query = query.range(offset, offset + limit - 1);
    } else if (typeof limit === 'number') {
      query = query.limit(limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async listProfileIdsByTeam(orgId: string, projectId: string, team: string): Promise<string[]> {
    const rows = await fetchAllPaged<{ profile_id: string }>((from, to) =>
      this.supabase
        .from('memberships')
        .select('profile_id')
        .eq('org_id', orgId)
        .eq('project_id', projectId)
        .eq('metadatas->>team', team)
        .order('profile_id')
        .range(from, to),
    );
    return rows.map((row) => row.profile_id);
  }

  /** search_profiles_of_project when projectId is given, else search_profiles_of_org
   * — both dedupe to one row per profile (most recent membership) and rank by
   * pgroonga_score against profiles.search_full when search_query is non-empty. */
  async searchMembers(orgId: string, options?: { projectId?: string; search?: string; limit?: number }): Promise<any[]> {
    const fn = options?.projectId ? 'search_profiles_of_project' : 'search_profiles_of_org';
    const params: Record<string, any> = {
      p_org_id: orgId,
      search_query: options?.search ?? '',
      row_limit: options?.limit ?? 30,
    };
    if (options?.projectId) params.p_project_id = options.projectId;
    const { data, error } = await this.supabase.rpc(fn, params);
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async searchProfiles(options?: { search?: string; provinceCode?: string }): Promise<any[]> {
    const { data, error } = await this.supabase.rpc('search_profiles_for_sourcing', {
      search_query: options?.search ?? '',
      p_province_code: options?.provinceCode ?? null,
    });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async searchProfilesByContactExact(query: string, orgId?: string, projectId?: string): Promise<any[]> {
    const { data, error } = await this.supabase.rpc('search_profiles_by_contact_exact', {
      search_query: query,
      p_org_id: orgId ?? null,
      p_project_id: projectId ?? null,
    });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async bulkAddMembers(orgId: string, projectId: string | null, profileIds: string[], roleId: string, metadatas?: Record<string, any> | null) {
    const rows = profileIds.map((profileId) => ({
      org_id: orgId, project_id: projectId ?? null, profile_id: profileId, role_id: roleId,
      ...(metadatas ? { metadatas } : {}),
    }));
    const { error } = await this.supabase.from('memberships').insert(rows);
    if (error) throw new Error(error.message);
  }

  async createUserWithMembership(payload: CreateUserPayload): Promise<{ userId: string; email: string }> {
    const { data, error } = await this.supabase.functions.invoke('create-user', {
      body: toCreateUserBody(payload),
    });
    if (error) throw new Error(await extractEdgeFunctionError(error, 'create-user failed'));
    if (data?.error) throw new Error(data.error);
    return { userId: data.userId, email: data.email };
  }

  async createUsersBulk(payload: CreateUserPayload[]): Promise<Array<{ email: string; ok: boolean; userId?: string; error?: string }>> {
    if (!payload.length) return [];
    const { data, error } = await this.supabase.functions.invoke('create-user', {
      body: { users: payload.map(toCreateUserBody) },
    });
    if (error) throw new Error(await extractEdgeFunctionError(error, 'create-user bulk failed'));
    if (data?.error) throw new Error(data.error);
    return (data?.results ?? []) as Array<{ email: string; ok: boolean; userId?: string; error?: string }>;
  }

  async removeMember(membershipId: string) {
    const { error } = await this.supabase.from('memberships').delete().eq('id', membershipId);
    if (error) throw new Error(error.message);
  }

  async listInvitations(orgId: string, projectId?: string) {
    let query = this.supabase
      .from('invitations')
      .select(`id, org_id, project_id, session_id, role_id, code, note, expires_at, max_uses, used_count, is_active, created_at, role:roles(name), project:projects(name)`)
      .eq('org_id', orgId);
    if (projectId) query = query.eq('project_id', projectId);
    query = query.order('created_at', { ascending: false });
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async createInvitation(payload: {
    org_id: string; role_id: string; project_id?: string | null; session_id?: string | null;
    note?: string | null; expires_at?: string | null; max_uses?: number | null; created_by: string;
  }) {
    const code = Math.random().toString(36).slice(2, 10).toUpperCase();
    const { data, error } = await this.supabase
      .from('invitations')
      .insert({ ...payload, code })
      .select(`id, org_id, project_id, session_id, role_id, code, note, expires_at, max_uses, used_count, is_active, created_at, role:roles(name)`)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  }

  async deactivateInvitation(id: string) {
    const { error } = await this.supabase.from('invitations').update({ is_active: false }).eq('id', id);
    if (error) throw new Error(error.message);
  }

  async listRoles(orgId: string) {
    const { data, error } = await this.supabase
      .from('roles')
      .select(`id, org_id, name, description`)
      .or(`org_id.eq.${orgId},org_id.is.null`)
      .order('name');
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async createRole(orgId: string, name: string, description?: string | null) {
    const { data, error } = await this.supabase
      .from('roles')
      .insert({ org_id: orgId, name, description: description ?? null })
      .select('id, org_id, name, description')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  }

  async updateRole(id: string, name: string, description?: string | null) {
    const { error } = await this.supabase
      .from('roles')
      .update({ name, description: description ?? null })
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  async deleteRole(id: string) {
    const { error } = await this.supabase.from('roles').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async listPermissions(orgId: string, options?: { limit?: number; offset?: number; search?: string }) {
    const { data, error } = await this.supabase.rpc('search_permissions', {
      p_org_id: orgId,
      search_query: options?.search ?? '',
      row_limit: options?.limit ?? 500,
      row_offset: options?.offset ?? 0,
    });
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async createPermission(orgId: string, resource: string, action: string) {
    const { data, error } = await this.supabase
      .from('permissions')
      .insert({ org_id: orgId, resource, action })
      .select('id, org_id, resource, action')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  }

  async updatePermission(id: string, resource: string, action: string) {
    const { error } = await this.supabase
      .from('permissions')
      .update({ resource, action })
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  async deletePermission(id: string) {
    const { error } = await this.supabase.from('permissions').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async listPermissionsForRoles(roleIds: string[]): Promise<Record<string, any[]>> {
    if (!roleIds.length) return {};
    const { data, error } = await this.supabase
      .from('role_permissions')
      .select('role_id, permission:permissions(id, org_id, resource, action)')
      .in('role_id', roleIds);
    if (error) throw new Error(error.message);
    const map: Record<string, any[]> = {};
    for (const r of data ?? []) {
      if (!r.permission) continue;
      const bucket = map[r.role_id] ?? (map[r.role_id] = []);
      bucket.push(r.permission);
    }
    return map;
  }

  async listPermissionsOfRole(roleId: string) {
    const { data, error } = await this.supabase
      .from('role_permissions')
      .select('permission_id, permission:permissions(id, org_id, resource, action)')
      .eq('role_id', roleId);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => r.permission).filter(Boolean);
  }

  async assignPermissionToRole(roleId: string, permissionId: string) {
    const { error } = await this.supabase
      .from('role_permissions')
      .insert({ role_id: roleId, permission_id: permissionId });
    if (error) throw new Error(error.message);
  }

  async removePermissionFromRole(roleId: string, permissionId: string) {
    const { error } = await this.supabase
      .from('role_permissions')
      .delete()
      .eq('role_id', roleId)
      .eq('permission_id', permissionId);
    if (error) throw new Error(error.message);
  }

  async updateMemberRole(membershipId: string, roleId: string) {
    const { error } = await this.supabase
      .from('memberships')
      .update({ role_id: roleId })
      .eq('id', membershipId);
    if (error) throw new Error(error.message);
  }

  async updateMemberMetadata(membershipId: string, metadatas: Record<string, any>) {
    const { error } = await this.supabase
      .from('memberships')
      .update({ metadatas })
      .eq('id', membershipId);
    if (error) throw new Error(error.message);
  }

  async getInvitationByCode(code: string): Promise<any | null> {
    const { data, error } = await this.supabase
      .from('invitations')
      .select(`id, org_id, project_id, session_id, role_id, code, note, expires_at, max_uses, used_count, is_active, created_at, role:roles(name), org:orgs(name), project:projects(name)`)
      .eq('code', code)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async checkExistingEmails(emails: string[]): Promise<string[]> {
    if (!emails.length) return [];
    const { data, error } = await this.supabase
      .from('profiles')
      .select('email')
      .in('email', emails);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => r.email as string);
  }

  async redeemInvitation(code: string, profileId: string): Promise<void> {
    const { error } = await this.supabase.rpc('redeem_invitation', {
      p_code: code,
      p_profile_id: profileId,
    });
    if (error) throw new Error(error.message);
  }

  async exportPermissionTemplate(orgId: string): Promise<any> {
    const { data, error } = await this.supabase
      .rpc('export_org_permission_template', { p_org_id: orgId });
    if (error) throw new Error(error.message);
    return data;
  }

  async importPermissionTemplate(orgId: string, template: any): Promise<void> {
    const { error } = await this.supabase
      .rpc('import_org_permission_template', { p_org_id: orgId, p_template: template });
    if (error) throw new Error(error.message);
  }
}
